<template>
  <div class="rooms">
    <h1 class="rooms-title">{{ t('rooms.title') }}</h1>
    <p class="rooms-lead">{{ t('rooms.lead') }}</p>

    <div class="deck-line">
      <label class="deck-pick">
        <span>{{ t('lobby.playDeck') }}</span>
        <select
          v-if="deckOptions.length > 0"
          class="deck-select"
          :value="activeDeckId"
          :aria-label="t('lobby.playDeck')"
          @change="onActiveDeckChange"
        >
          <option
            v-for="opt in deckOptions"
            :key="opt.id"
            :value="opt.id"
            :disabled="!opt.playable && opt.id !== activeDeckId"
          >
            {{ opt.name }}{{ opt.playable ? '' : t('lobby.deckNotReady') }}
          </option>
        </select>
        <span v-else>{{ t('deck.buildForty') }}</span>
      </label>
      <router-link to="/deck-builder" class="deck-edit">
        {{
          deckOptions.some((opt) => opt.playable)
            ? t('lobby.edit')
            : t('lobby.buildDeck')
        }}
      </router-link>
    </div>

    <div class="actions">
      <button type="button" class="btn-primary" @click="createRoom">
        {{ t('rooms.create') }}
      </button>
    </div>

    <form class="code-form" @submit.prevent="joinTypedCode">
      <label class="code-label" for="room-code-input">
        {{ t('rooms.joinCode') }}
      </label>
      <div class="code-row">
        <input
          id="room-code-input"
          v-model="codeInput"
          class="code-input"
          type="text"
          maxlength="6"
          autocomplete="off"
          spellcheck="false"
          autocapitalize="characters"
          :placeholder="t('rooms.codePlaceholder')"
          :aria-invalid="codeError ? 'true' : 'false'"
        >
        <button type="submit" class="btn-primary">
          {{ t('rooms.join') }}
        </button>
      </div>
      <p v-if="codeError" class="code-error">{{ codeError }}</p>
    </form>

    <p v-if="listError" class="list-error">{{ t('rooms.error') }}</p>
    <p v-else-if="rows === null" class="rooms-empty">{{ t('rooms.connecting') }}</p>
    <p v-else-if="visibleRows.length === 0" class="rooms-empty">
      {{ t('rooms.empty') }}
    </p>
    <ul v-else class="room-list">
      <li v-for="row in visibleRows" :key="row.code" class="room-row">
        <AvatarView
          :config="row.hostAvatar"
          crop="bust"
          circle
          :size="52"
          :label="row.hostName"
        />
        <div class="room-meta">
          <p class="room-host">{{ row.hostName }}</p>
          <p class="room-sub">
            <span class="room-code">{{ row.code }}</span>
            <span>{{ t('rooms.seats', { taken: row.seatsTaken, max: row.seatsMax }) }}</span>
          </p>
        </div>
        <span v-if="isMine(row)" class="room-yours">{{ t('rooms.yours') }}</span>
        <button
          v-else
          type="button"
          class="btn-primary join-btn"
          @click="joinCode(row.code)"
        >
          {{ t('rooms.join') }}
        </button>
      </li>
    </ul>

    <router-link to="/lobby" class="rooms-back">
      {{ t('rooms.back') }}
    </router-link>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { PrivateRoomRow } from '@shared/index'
import { activateFirstPlayable, listDeckPlayOptions } from '../game/localDeck'
import { useDeckStore } from '../stores/deck'
import { DuelSfx } from '../game/audio/DuelSfx'
import { armDuelLandscape } from '../composables/useDuelOrientation'
import { useI18n } from '../i18n/t'
import { usePlayerStore } from '../stores/player'
import AvatarView from '../components/AvatarView.vue'
import { RoomBrowserClient } from '../game/net/RoomBrowserClient'

const CODE_CHARS = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{4}$/

const { t } = useI18n()
const router = useRouter()
const player = usePlayerStore()
const deckStore = useDeckStore()
activateFirstPlayable()
const deckOptions = computed(() => listDeckPlayOptions())
const activeDeckId = computed(() => deckStore.activeId || '')

const rows = ref<PrivateRoomRow[] | null>(null)
const listError = ref(false)
const codeInput = ref('')
const codeError = ref('')

let browser: RoomBrowserClient | null = null

const visibleRows = computed(() => rows.value ?? [])

function isMine(row: PrivateRoomRow): boolean {
  return row.hostName === player.username
}

function onActiveDeckChange(event: Event): void {
  const id = (event.target as HTMLSelectElement).value
  const opt = deckOptions.value.find((item) => item.id === id)
  if (!opt?.playable) return
  deckStore.setActiveId(id)
}

function goDuel(query: Record<string, string>): void {
  DuelSfx.prime()
  armDuelLandscape()
  router.push({
    path: '/online-duel',
    query: { ...query, username: player.username },
  })
}

function createRoom(): void {
  goDuel({ mode: 'create' })
}

/**
 * Strips spaces and forces the 4-character room alphabet.
 */
function normalizeCode(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 4)
}

function joinCode(code: string): void {
  const next = normalizeCode(code)
  if (!CODE_CHARS.test(next)) {
    codeError.value = t('rooms.badCode')
    return
  }
  codeError.value = ''
  goDuel({ mode: 'join', code: next })
}

function joinTypedCode(): void {
  joinCode(codeInput.value)
}

onMounted(() => {
  browser = new RoomBrowserClient({
    onList: (list) => {
      listError.value = false
      rows.value = list
    },
    onError: () => {
      listError.value = true
    },
  })
})

onUnmounted(() => {
  browser?.dispose()
  browser = null
})
</script>

<style scoped>
.rooms {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  min-height: calc(100dvh - var(--app-nav-offset, 64px));
  padding: 24px 16px 48px;
}

.rooms-title {
  margin: 0;
  color: var(--md-sys-color-primary);
  font-size: 36px;
  font-weight: 700;
  text-align: center;
}

.rooms-lead {
  margin: -8px 0 0;
  max-width: 28rem;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.deck-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.deck-pick {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.deck-select {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
}

.deck-edit,
.rooms-back {
  color: var(--md-sys-color-primary);
  font-size: 14px;
}

.deck-edit:hover,
.rooms-back:hover {
  color: var(--md-sys-color-on-surface);
}

.rooms-back {
  color: var(--md-sys-color-on-surface-variant);
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.code-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(420px, 100%);
}

.code-label {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
  font-weight: 500;
}

.code-row {
  display: flex;
  gap: 8px;
}

.code-input {
  flex: 1;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.code-error,
.list-error {
  margin: 0;
  color: var(--md-sys-color-error, #b3261e);
  font-size: 13px;
}

.rooms-empty {
  margin: 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  text-align: center;
}

.room-list {
  list-style: none;
  margin: 0;
  padding: 0;
  width: min(520px, 100%);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.room-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
  box-shadow: var(--md-elevation-1);
}

.room-meta {
  flex: 1;
  min-width: 0;
}

.room-host {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--md-sys-color-on-surface);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.room-sub {
  margin: 4px 0 0;
  display: flex;
  gap: 10px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
}

.room-code {
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--md-sys-color-primary);
}

.room-yours {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
  font-weight: 600;
}

.join-btn {
  flex-shrink: 0;
}

.btn-primary {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-weight: 700;
  border-radius: 8px;
  padding: 12px 24px;
  min-height: 44px;
  cursor: pointer;
  border: 0;
  font-size: inherit;
  font-family: inherit;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

@media (max-width: 720px) {
  .rooms-title {
    font-size: 28px;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .rooms {
    gap: 12px;
    padding: 16px;
  }

  .rooms-title {
    font-size: 24px;
  }
}
</style>
